import React, { ReactNode } from 'react';
import { Order } from '../../../../types/interfaces';
import { formatAddress } from '@shared/lib/format-address';
import {
  deliveryTypes,
  deliveryTypes2,
  payerTypes,
  recipientTypes,
  selfDeliveryTypes,
} from '@shared/config/delivery-options';
import { getLabel } from '../lib/getLabel';
import { getTaxLabel } from '@shared/lib/tax-label';

// Раньше «Информация о доставке» рисовалась широкой таблицей с
// 5–7 колонками: заголовки переносились по 2–3 строки, длинный
// адрес ломал верстку. Перевели на сетку «label → value» в стиле
// карточек на других экранах (Company.tsx, контакты): строгий
// uppercase-лейбл сверху, значение снизу, в строке умещается 2–3
// поля. Длинные строки (адрес) переносятся естественно через
// break-words.

const Field: React.FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <dt className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
      {label}
    </dt>
    <dd className="text-sm text-[#2d2c2c] break-words">{value}</dd>
  </div>
);

const Placeholder = () => (
  <span className="text-gray-400">Информация скоро будет</span>
);

const valueOr = (raw: ReactNode | null | undefined): ReactNode => {
  if (raw === null || raw === undefined || raw === '') return <Placeholder />;
  return raw;
};

export function DeliveryInfo({ order }: { order: Order }) {
  const isSelfDelivery = order.delivery_type === 'self-delivery';
  return isSelfDelivery ? (
    <SelfDelivery order={order} />
  ) : (
    <CommonDelivery order={order} />
  );
}

function CommonDelivery({ order }: { order: Order }) {
  const isThirdParty = order.recipient_type === 'third party';
  const isPayerSender = order.payer_type === 'sender';

  const deliveryTypeLabel = isPayerSender
    ? getLabel(order.delivery_type, deliveryTypes2)
    : getLabel(order.delivery_type, deliveryTypes);

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
      <Field
        label="Трек-номер"
        value={valueOr(order.delivery_track_number)}
      />
      <Field label="Тип доставки" value={valueOr(deliveryTypeLabel)} />
      <Field
        label="Дата доставки"
        value={valueOr(order.planned_shipment_date)}
      />
      <Field
        label="Тип получателя"
        value={valueOr(getLabel(order.recipient_type, recipientTypes))}
      />
      {isThirdParty && (() => {
        const taxLabel = getTaxLabel(
          (order as any)?.legal_entity_data?.country
        );
        return (
          <Field
            label="Третья сторона"
            value={
              <>
                {order.third_party_company_name || '—'}
                {order.third_party_company_inn && (
                  <>
                    {', '}{taxLabel}{' '}
                    <b>{order.third_party_company_inn}</b>
                  </>
                )}
              </>
            }
          />
        );
      })()}
      <Field
        label="Тип плательщика"
        value={valueOr(getLabel(order.payer_type, payerTypes))}
      />
      <Field label="Адрес доставки" value={valueOr(formatAddress(order))} />
      <Field
        label="Статус доставки"
        value={valueOr(order.delivery_state)}
      />
    </dl>
  );
}

function SelfDelivery({ order }: { order: Order }) {
  const isConfidant = order.self_delivery_type === 'confidant';
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
      <Field label="Тип доставки" value="Самовывоз" />
      <Field
        label="Заказ заберёт"
        value={valueOr(getLabel(order.self_delivery_type, selfDeliveryTypes))}
      />
      {isConfidant ? (
        <>
          <Field
            label="ФИО доверенного лица"
            value={valueOr(order.confidant_name)}
          />
          <Field
            label="Номер доверенности"
            value={valueOr(order.confidant_number)}
          />
        </>
      ) : (
        <>
          <Field
            label="Курьерская служба"
            value={valueOr(order.courier_service)}
          />
          <Field
            label="Номер или фото накладной"
            value={valueOr(order.invoice_url)}
          />
        </>
      )}
    </dl>
  );
}
